import { storeToRefs } from 'pinia';

import useAttribution from '@/composables/useAttribution';
import useEcosystemInsights from '@/composables/useEcosystemInsights';
import { ATTRIBUTION_METRIC_TYPES } from '@/constants/attribution';
import { useEcosystemInsightsStore } from '@/stores';

export default function usePartnerImpact() {
  const {
    WIN_RATE_WITH_PARTNER,
    DEAL_SIZE_WITH_PARTNER,
    TIME_TO_CLOSE_WITH_PARTNER,
    WIN_RATE_WITHOUT_PARTNERS,
    DEAL_SIZE_WITHOUT_PARTNERS,
    TIME_TO_CLOSE_WITHOUT_PARTNERS,
    WIN_RATE_WITH_PARTNERS,
    DEAL_SIZE_WITH_PARTNERS,
    TIME_TO_CLOSE_WITH_PARTNERS,
  } = ATTRIBUTION_METRIC_TYPES;

  const { currencyFormatter, percentFormatter, durationFormatter } =
    useAttribution();

  const { formatMultiplier } = useEcosystemInsights();
  const eiStore = useEcosystemInsightsStore();
  const { ecosystemMetrics } = storeToRefs(eiStore);

  function getMultiplierLookup(isPartnerScore) {
    return {
      [DEAL_SIZE_WITH_PARTNER]: isPartnerScore
        ? 'deal_size_with_partner'
        : 'deal_size_multiplier',
      [WIN_RATE_WITH_PARTNER]: isPartnerScore
        ? 'win_rate_with_partner'
        : 'win_rate_multiplier',
      [TIME_TO_CLOSE_WITH_PARTNER]: isPartnerScore
        ? 'time_to_close_with_partner'
        : 'time_to_close_multiplier',
    };
  }

  function getEcosystemValueLookup(isPartnerScore) {
    return isPartnerScore
      ? {
          [WIN_RATE_WITH_PARTNER]: [WIN_RATE_WITH_PARTNERS, percentFormatter],
          [DEAL_SIZE_WITH_PARTNER]: [
            DEAL_SIZE_WITH_PARTNERS,
            currencyFormatter,
          ],
          [TIME_TO_CLOSE_WITH_PARTNER]: [
            TIME_TO_CLOSE_WITH_PARTNERS,
            durationFormatter,
          ],
        }
      : {
          [WIN_RATE_WITH_PARTNER]: [
            WIN_RATE_WITHOUT_PARTNERS,
            percentFormatter,
          ],
          [DEAL_SIZE_WITH_PARTNER]: [
            DEAL_SIZE_WITHOUT_PARTNERS,
            currencyFormatter,
          ],
          [TIME_TO_CLOSE_WITH_PARTNER]: [
            TIME_TO_CLOSE_WITHOUT_PARTNERS,
            durationFormatter,
          ],
        };
  }

  const computePhrases = (metrics, isPartnerScore = false) => {
    if (!metrics) return [];

    const multiplierLookup = getMultiplierLookup(isPartnerScore);
    const ecosystemValueLookup = getEcosystemValueLookup(isPartnerScore);

    const getMetricValues = (metricProperty) => {
      const [ecosystemValueKey, formatter] =
        ecosystemValueLookup[metricProperty];
      const partnerValue = metrics[metricProperty];
      const ecosystemValue = ecosystemMetrics.value[ecosystemValueKey];
      const unformattedMultiplier = metrics[multiplierLookup[metricProperty]];

      return {
        ecosystemValueKey,
        formatter,
        partnerValue,
        ecosystemValue,
        unformattedMultiplier,
      };
    };

    const isMetricBetter = (metricProperty, partnerValue, ecosystemValue) =>
      metricProperty === TIME_TO_CLOSE_WITH_PARTNER
        ? partnerValue < ecosystemValue
        : partnerValue > ecosystemValue;

    const getFormattedDifference = (formatter, partnerValue, ecosystemValue) =>
      formatter({ value: Math.abs(partnerValue - ecosystemValue) });

    const SAME_AS = 'about the same as';

    const getComparisonText = (
      metricProperty,
      partnerValue,
      ecosystemValue,
    ) => {
      if (partnerValue == null || ecosystemValue == null) return null;

      const diff = Math.abs(partnerValue - ecosystemValue);
      const isBetter = isMetricBetter(
        metricProperty,
        partnerValue,
        ecosystemValue,
      );

      if (metricProperty === WIN_RATE_WITH_PARTNER) {
        if (!Math.round(diff * 100)) return SAME_AS;
        const formatted = percentFormatter({ value: diff, fractionDigits: 0 });
        return `${formatted} ${isBetter ? 'higher' : 'lower'} than`;
      }

      if (metricProperty === TIME_TO_CLOSE_WITH_PARTNER) {
        if (!Math.round(diff)) return SAME_AS;
        const formatted = durationFormatter({ value: diff });
        return `${formatted} ${isBetter ? 'faster' : 'slower'} than`;
      }

      if (!ecosystemValue) return null;
      const pctChange = (diff / Math.abs(ecosystemValue)) * 100;
      if (!Math.round(pctChange)) return SAME_AS;
      const formatted = `${Math.round(pctChange)}%`;
      return `${formatted} ${isBetter ? 'larger' : 'smaller'} than`;
    };

    const buildPhrase = (metricProperty) => {
      const { formatter, partnerValue, ecosystemValue, unformattedMultiplier } =
        getMetricValues(metricProperty);
      const isBetter = isMetricBetter(
        metricProperty,
        partnerValue,
        ecosystemValue,
      );
      const formattedDifference = getFormattedDifference(
        formatter,
        partnerValue,
        ecosystemValue,
      );

      const formattedValue = {
        [WIN_RATE_WITH_PARTNER]: isPartnerScore
          ? percentFormatter({
              value: partnerValue,
              fractionDigits: 0,
            })
          : formatMultiplier(unformattedMultiplier),
        [DEAL_SIZE_WITH_PARTNER]: isPartnerScore
          ? formatter({ value: partnerValue })
          : formattedDifference,
        [TIME_TO_CLOSE_WITH_PARTNER]: isPartnerScore
          ? formatter({ value: partnerValue })
          : formattedDifference,
      }[metricProperty];

      const better = {
        [TIME_TO_CLOSE_WITH_PARTNER]: isBetter ? 'faster' : 'slower',
        [DEAL_SIZE_WITH_PARTNER]: isBetter ? 'higher' : 'lower',
        [WIN_RATE_WITH_PARTNER]: isBetter ? 'higher' : 'lower',
      }[metricProperty];

      const metricLabel = {
        [WIN_RATE_WITH_PARTNER]: 'win rate',
        [DEAL_SIZE_WITH_PARTNER]: 'average opportunity size',
        [TIME_TO_CLOSE_WITH_PARTNER]: 'average time to close',
      }[metricProperty];

      const prefix = {
        [WIN_RATE_WITH_PARTNER]: 'Your win rate is',
        [DEAL_SIZE_WITH_PARTNER]: 'Your average opportunity size is',
        [TIME_TO_CLOSE_WITH_PARTNER]: 'Your average time to close is',
      }[metricProperty];

      const suffix = {
        [WIN_RATE_WITH_PARTNER]: 'win rate',
        [DEAL_SIZE_WITH_PARTNER]: 'opp size',
        [TIME_TO_CLOSE_WITH_PARTNER]: 'time to close',
      }[metricProperty];

      const comparisonText = isPartnerScore
        ? getComparisonText(metricProperty, partnerValue, ecosystemValue)
        : undefined;

      return {
        better,
        comparisonText,
        formattedValue,
        metricLabel,
        unformattedMultiplier,
        prefix,
        suffix,
        key: metricProperty,
        multiplier: multiplierLookup[metricProperty],
      };
    };
    return [
      WIN_RATE_WITH_PARTNER,
      DEAL_SIZE_WITH_PARTNER,
      TIME_TO_CLOSE_WITH_PARTNER,
    ]
      .filter((metricKey) => metrics[multiplierLookup[metricKey]] !== null)
      .map(buildPhrase);
  };
  return { computePhrases };
}
